import React, { useState, useEffect, useCallback } from 'react';
import {
  ShieldCheck, ShieldAlert, RefreshCw, Download, AlertTriangle,
  CheckCircle, XCircle, Clock, Package, Activity, Settings,
  ChevronDown, ChevronUp, Loader2, ArrowUpCircle, Info,
} from 'lucide-react';

// ── Severity color mapping ──────────────────────────────────────────────────
const SEV_COLORS = {
  critical: { bg: '#fef2f2', border: '#fca5a5', text: '#991b1b', badge: '#dc2626' },
  high:     { bg: '#fff7ed', border: '#fdba74', text: '#9a3412', badge: '#ea580c' },
  moderate: { bg: '#fffbeb', border: '#fcd34d', text: '#92400e', badge: '#d97706' },
  low:      { bg: '#f0fdf4', border: '#86efac', text: '#166534', badge: '#16a34a' },
  info:     { bg: '#eff6ff', border: '#93c5fd', text: '#1e40af', badge: '#2563eb' },
};

function SeverityBadge({ severity, count }) {
  const c = SEV_COLORS[severity] || SEV_COLORS.info;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
      background: c.bg, color: c.text, border: `1px solid ${c.border}`,
    }}>
      {severity.charAt(0).toUpperCase() + severity.slice(1)}
      {count !== undefined && <span style={{
        background: c.badge, color: '#fff', borderRadius: 8, padding: '0 6px',
        fontSize: 11, marginLeft: 2,
      }}>{count}</span>}
    </span>
  );
}

function StatCard({ icon: Icon, label, value, color, sub }) {
  return (
    <div style={{
      background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12,
      padding: '20px 24px', flex: '1 1 200px', minWidth: 180,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 10, display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          background: `${color}15`,
        }}>
          <Icon size={18} color={color} />
        </div>
        <span style={{ fontSize: 13, color: '#6b7280', fontWeight: 500 }}>{label}</span>
      </div>
      <div style={{ fontSize: 28, fontWeight: 700, color: '#111827' }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

export default function SecurityUpdatesPage() {
  const [auditReport, setAuditReport] = useState(null);
  const [outdatedReport, setOutdatedReport] = useState(null);
  const [history, setHistory] = useState([]);
  const [nodeInfo, setNodeInfo] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState({ audit: false, outdated: false, update: false });
  const [expandedVuln, setExpandedVuln] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [updateResult, setUpdateResult] = useState(null);
  const [activeTab, setActiveTab] = useState('vulnerabilities');

  const api = window.zignagenixAPI;

  // ── Load initial data ──────────────────────────────────────────────────────
  useEffect(() => {
    loadHistory();
    loadNodeInfo();
    loadConfig();
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const res = await api.getSecurityHistory({ limit: 20 });
      if (res?.success) setHistory(res.records || []);
    } catch (err) { console.error('[Security] History load failed:', err); }
  }, [api]);

  const loadNodeInfo = useCallback(async () => {
    try {
      const res = await api.getSecurityNodeInfo();
      if (res?.success) setNodeInfo(res);
    } catch (err) { console.error('[Security] Node info failed:', err); }
  }, [api]);

  const loadConfig = useCallback(async () => {
    try {
      const res = await api.getSecurityConfig();
      if (res?.success) setConfig(res.config);
    } catch (err) { console.error('[Security] Config load failed:', err); }
  }, [api]);

  // ── Actions ────────────────────────────────────────────────────────────────
  const runAudit = useCallback(async () => {
    setLoading(p => ({ ...p, audit: true }));
    try {
      const res = await api.runSecurityAudit();
      if (res?.success) {
        setAuditReport(res.report);
        loadHistory();
      }
    } catch (err) { console.error('[Security] Audit failed:', err); }
    setLoading(p => ({ ...p, audit: false }));
  }, [api, loadHistory]);

  const checkOutdated = useCallback(async () => {
    setLoading(p => ({ ...p, outdated: true }));
    try {
      const res = await api.checkSecurityOutdated();
      if (res?.success) {
        setOutdatedReport(res.report);
        loadHistory();
      }
    } catch (err) { console.error('[Security] Outdated check failed:', err); }
    setLoading(p => ({ ...p, outdated: false }));
  }, [api, loadHistory]);

  const runUpdate = useCallback(async (mode) => {
    if (!window.confirm(`Run ${mode === 'audit-fix' ? 'npm audit fix' : 'npm update (safe patches)'}? This will modify node_modules.`)) return;
    setLoading(p => ({ ...p, update: true }));
    setUpdateResult(null);
    try {
      const res = await api.runSecurityUpdate({ mode });
      setUpdateResult(res);
      loadHistory();
      // Refresh audit after update
      if (res?.postUpdateAudit) setAuditReport(res.postUpdateAudit);
    } catch (err) {
      console.error('[Security] Update failed:', err);
      setUpdateResult({ success: false, output: err.message });
    }
    setLoading(p => ({ ...p, update: false }));
  }, [api, loadHistory]);

  const saveConfig = useCallback(async (key, value) => {
    try {
      await api.setSecurityConfig(key, value);
      setConfig(prev => ({ ...prev, [key]: value }));
    } catch (err) { console.error('[Security] Config save failed:', err); }
  }, [api]);

  // ── Derived values ─────────────────────────────────────────────────────────
  const totalVulns = auditReport?.total || 0;
  const critCount = auditReport?.counts?.critical || 0;
  const highCount = auditReport?.counts?.high || 0;
  const outdatedCount = outdatedReport?.total || 0;

  const overallStatus = !auditReport ? 'unknown'
    : critCount > 0 ? 'critical'
    : highCount > 0 ? 'warning'
    : totalVulns > 0 ? 'info'
    : 'secure';

  const statusColors = {
    unknown: '#6b7280', critical: '#dc2626', warning: '#ea580c', info: '#d97706', secure: '#16a34a',
  };
  const statusLabels = {
    unknown: 'Not Scanned', critical: 'Critical Issues', warning: 'High Severity Issues',
    info: 'Minor Issues', secure: 'All Clear',
  };

  return (
    <div style={{ padding: '32px 40px', maxWidth: 1200, margin: '0 auto' }}>
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{
            width: 48, height: 48, borderRadius: 14, display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            background: `${statusColors[overallStatus]}15`,
          }}>
            {overallStatus === 'secure' ? <ShieldCheck size={26} color={statusColors.secure} /> : <ShieldAlert size={26} color={statusColors[overallStatus]} />}
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#111827' }}>
              Security & Dependency Manager
            </h1>
            <span style={{ fontSize: 13, color: statusColors[overallStatus], fontWeight: 600 }}>
              {statusLabels[overallStatus]}
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => setShowSettings(s => !s)} style={btnStyle('#6b7280')}>
            <Settings size={15} /> Config
          </button>
          <button onClick={runAudit} disabled={loading.audit} style={btnStyle('#2E8B98')}>
            {loading.audit ? <Loader2 size={15} className="spin" /> : <RefreshCw size={15} />}
            {loading.audit ? 'Scanning…' : 'Run Audit'}
          </button>
          <button onClick={checkOutdated} disabled={loading.outdated} style={btnStyle('#7c3aed')}>
            {loading.outdated ? <Loader2 size={15} className="spin" /> : <Package size={15} />}
            {loading.outdated ? 'Checking…' : 'Check Updates'}
          </button>
        </div>
      </div>

      {/* ── Settings Panel ─────────────────────────────────────────────────── */}
      {showSettings && (
        <div style={{
          background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 12,
          padding: 20, marginBottom: 20,
        }}>
          <h3 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 600, color: '#374151' }}>
            Auto-Check Configuration
          </h3>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#4b5563' }}>
              <input type="checkbox" checked={config?.autoCheckEnabled ?? true}
                onChange={e => saveConfig('autoCheckEnabled', e.target.checked)} />
              Auto-check on startup
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#4b5563' }}>
              <input type="checkbox" checked={config?.notifyOnCritical ?? true}
                onChange={e => saveConfig('notifyOnCritical', e.target.checked)} />
              Alert on critical vulnerabilities
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#4b5563' }}>
              <input type="checkbox" checked={config?.autoFixPatch ?? false}
                onChange={e => saveConfig('autoFixPatch', e.target.checked)} />
              Auto-fix patch updates on startup
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#4b5563' }}>
              Check interval:
              <select value={config?.checkIntervalHours ?? 24}
                onChange={e => saveConfig('checkIntervalHours', parseInt(e.target.value))}
                style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13 }}>
                <option value={6}>Every 6 hours</option>
                <option value={12}>Every 12 hours</option>
                <option value={24}>Daily</option>
                <option value={168}>Weekly</option>
              </select>
            </label>
          </div>
        </div>
      )}

      {/* ── Node/Runtime Info ───────────────────────────────────────────────── */}
      {nodeInfo && (
        <div style={{
          display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 20,
          padding: '12px 16px', background: '#f0f9ff', borderRadius: 10,
          border: '1px solid #bae6fd', fontSize: 13, color: '#0369a1',
        }}>
          <span><strong>Node:</strong> {nodeInfo.node}</span>
          <span><strong>npm:</strong> {nodeInfo.npm}</span>
          <span><strong>Electron:</strong> {nodeInfo.electron}</span>
          <span><strong>Chromium:</strong> {nodeInfo.chrome}</span>
        </div>
      )}

      {/* ── Summary Stats ──────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
        <StatCard icon={ShieldAlert} label="Vulnerabilities" value={totalVulns >= 0 ? totalVulns : '—'}
          color={critCount > 0 ? '#dc2626' : highCount > 0 ? '#ea580c' : '#16a34a'}
          sub={auditReport ? `${critCount} critical · ${highCount} high` : 'Run audit to scan'} />
        <StatCard icon={Package} label="Outdated Packages" value={outdatedCount >= 0 ? outdatedCount : '—'}
          color={outdatedCount > 10 ? '#ea580c' : outdatedCount > 0 ? '#d97706' : '#16a34a'}
          sub={outdatedReport ? `${outdatedReport.packages?.filter(p => p.isMajor).length || 0} major updates` : 'Check for updates'} />
        <StatCard icon={Activity} label="Total Scans" value={history.length}
          color="#7c3aed"
          sub={history[0] ? `Last: ${new Date(history[0].scanned_at).toLocaleString()}` : 'No scans yet'} />
        <StatCard icon={CheckCircle} label="Status"
          value={overallStatus === 'secure' ? 'Secure' : overallStatus === 'unknown' ? 'Unknown' : 'Issues'}
          color={statusColors[overallStatus]}
          sub={overallStatus === 'secure' ? 'All dependencies pass' : overallStatus === 'unknown' ? 'Awaiting first scan' : 'Action recommended'} />
      </div>

      {/* ── Update Actions ─────────────────────────────────────────────────── */}
      {auditReport && totalVulns > 0 && (
        <div style={{
          background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 12,
          padding: 16, marginBottom: 20, display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', flexWrap: 'wrap', gap: 12,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <AlertTriangle size={20} color="#d97706" />
            <span style={{ fontSize: 14, color: '#92400e', fontWeight: 500 }}>
              {totalVulns} vulnerabilit{totalVulns === 1 ? 'y' : 'ies'} detected — recommended action:
            </span>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => runUpdate('audit-fix')} disabled={loading.update}
              style={btnStyle('#d97706')}>
              {loading.update ? <Loader2 size={14} className="spin" /> : <ArrowUpCircle size={14} />}
              npm audit fix
            </button>
            <button onClick={() => runUpdate('safe')} disabled={loading.update}
              style={btnStyle('#16a34a')}>
              {loading.update ? <Loader2 size={14} className="spin" /> : <Download size={14} />}
              Safe Update (patch)
            </button>
          </div>
        </div>
      )}

      {/* ── Update Result ──────────────────────────────────────────────────── */}
      {updateResult && (
        <div style={{
          background: updateResult.success ? '#f0fdf4' : '#fef2f2',
          border: `1px solid ${updateResult.success ? '#86efac' : '#fca5a5'}`,
          borderRadius: 12, padding: 16, marginBottom: 20,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            {updateResult.success ? <CheckCircle size={18} color="#16a34a" /> : <XCircle size={18} color="#dc2626" />}
            <strong style={{ color: updateResult.success ? '#166534' : '#991b1b', fontSize: 14 }}>
              Update {updateResult.success ? 'completed' : 'encountered issues'}
            </strong>
          </div>
          {updateResult.output && (
            <pre style={{
              fontSize: 12, background: '#00000008', padding: 12, borderRadius: 8,
              maxHeight: 200, overflow: 'auto', whiteSpace: 'pre-wrap', margin: 0,
            }}>
              {updateResult.output}
            </pre>
          )}
        </div>
      )}

      {/* ── Tab Navigation ─────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid #e5e7eb', marginBottom: 20 }}>
        {[
          { id: 'vulnerabilities', label: 'Vulnerabilities', icon: ShieldAlert },
          { id: 'outdated', label: 'Outdated Packages', icon: Package },
          { id: 'history', label: 'Scan History', icon: Clock },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '10px 20px',
              fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer',
              background: 'transparent',
              color: activeTab === tab.id ? '#2E8B98' : '#6b7280',
              borderBottom: activeTab === tab.id ? '2px solid #2E8B98' : '2px solid transparent',
              marginBottom: -2, transition: 'all 0.15s',
            }}>
            <tab.icon size={15} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Vulnerabilities Tab ────────────────────────────────────────────── */}
      {activeTab === 'vulnerabilities' && (
        <div>
          {!auditReport && (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#9ca3af' }}>
              <ShieldCheck size={48} style={{ marginBottom: 12 }} />
              <p style={{ fontSize: 15, marginBottom: 16 }}>No vulnerability scan results yet.</p>
              <button onClick={runAudit} disabled={loading.audit} style={btnStyle('#2E8B98')}>
                <RefreshCw size={15} /> Run First Scan
              </button>
            </div>
          )}
          {auditReport && totalVulns === 0 && (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#16a34a' }}>
              <CheckCircle size={48} style={{ marginBottom: 12 }} />
              <p style={{ fontSize: 16, fontWeight: 600 }}>No vulnerabilities detected!</p>
              <p style={{ fontSize: 13, color: '#6b7280' }}>All dependencies passed the security audit.</p>
            </div>
          )}
          {auditReport?.vulnerabilities?.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {/* Severity filter row */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                {Object.entries(auditReport.counts || {}).filter(([, c]) => c > 0).map(([sev, count]) => (
                  <SeverityBadge key={sev} severity={sev} count={count} />
                ))}
              </div>
              {auditReport.vulnerabilities
                .sort((a, b) => {
                  const order = { critical: 0, high: 1, moderate: 2, low: 3, info: 4 };
                  return (order[a.severity] ?? 5) - (order[b.severity] ?? 5);
                })
                .map((vuln, idx) => {
                  const isExpanded = expandedVuln === idx;
                  const c = SEV_COLORS[vuln.severity] || SEV_COLORS.info;
                  return (
                    <div key={idx} style={{
                      border: `1px solid ${c.border}`, borderRadius: 10,
                      background: isExpanded ? c.bg : '#fff', overflow: 'hidden',
                      transition: 'background 0.15s',
                    }}>
                      <div onClick={() => setExpandedVuln(isExpanded ? null : idx)}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: '12px 16px', cursor: 'pointer',
                        }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <SeverityBadge severity={vuln.severity} />
                          <strong style={{ fontSize: 14, color: '#111827' }}>{vuln.package}</strong>
                          <span style={{ fontSize: 12, color: '#6b7280' }}>{vuln.title}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          {vuln.fixAvailable && (
                            <span style={{
                              fontSize: 11, color: '#16a34a', fontWeight: 600,
                              background: '#f0fdf4', padding: '2px 8px', borderRadius: 8,
                            }}>
                              Fix available
                            </span>
                          )}
                          {isExpanded ? <ChevronUp size={16} color="#9ca3af" /> : <ChevronDown size={16} color="#9ca3af" />}
                        </div>
                      </div>
                      {isExpanded && (
                        <div style={{ padding: '0 16px 14px', fontSize: 13, color: '#4b5563' }}>
                          {vuln.range && <p style={{ margin: '4px 0' }}><strong>Affected range:</strong> {vuln.range}</p>}
                          {vuln.directDependency && <p style={{ margin: '4px 0' }}><Info size={12} /> Direct dependency</p>}
                          {vuln.url && (
                            <p style={{ margin: '4px 0' }}>
                              <strong>Advisory:</strong>{' '}
                              <span style={{ color: '#2E8B98', textDecoration: 'underline' }}>{vuln.url}</span>
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      )}

      {/* ── Outdated Packages Tab ──────────────────────────────────────────── */}
      {activeTab === 'outdated' && (
        <div>
          {!outdatedReport && (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#9ca3af' }}>
              <Package size={48} style={{ marginBottom: 12 }} />
              <p style={{ fontSize: 15, marginBottom: 16 }}>No outdated package data yet.</p>
              <button onClick={checkOutdated} disabled={loading.outdated} style={btnStyle('#7c3aed')}>
                <Package size={15} /> Check Outdated
              </button>
            </div>
          )}
          {outdatedReport && outdatedReport.total === 0 && (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#16a34a' }}>
              <CheckCircle size={48} style={{ marginBottom: 12 }} />
              <p style={{ fontSize: 16, fontWeight: 600 }}>All packages are up to date!</p>
            </div>
          )}
          {outdatedReport?.packages?.length > 0 && (
            <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f9fafb' }}>
                    <th style={thStyle}>Package</th>
                    <th style={thStyle}>Current</th>
                    <th style={thStyle}>Wanted</th>
                    <th style={thStyle}>Latest</th>
                    <th style={thStyle}>Type</th>
                    <th style={thStyle}>Risk</th>
                  </tr>
                </thead>
                <tbody>
                  {outdatedReport.packages
                    .sort((a, b) => (b.isMajor ? 1 : 0) - (a.isMajor ? 1 : 0))
                    .map((pkg, idx) => (
                      <tr key={idx} style={{
                        borderTop: '1px solid #f3f4f6',
                        background: pkg.isMajor ? '#fffbeb' : '#fff',
                      }}>
                        <td style={tdStyle}>
                          <strong>{pkg.package}</strong>
                        </td>
                        <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{pkg.current}</td>
                        <td style={{ ...tdStyle, fontFamily: 'monospace', color: '#16a34a' }}>{pkg.wanted}</td>
                        <td style={{ ...tdStyle, fontFamily: 'monospace', color: pkg.isMajor ? '#dc2626' : '#0369a1' }}>
                          {pkg.latest}
                        </td>
                        <td style={tdStyle}>
                          <span style={{
                            fontSize: 11, padding: '2px 8px', borderRadius: 6,
                            background: pkg.type === 'devDependencies' ? '#eff6ff' : '#f0fdf4',
                            color: pkg.type === 'devDependencies' ? '#1e40af' : '#166534',
                          }}>
                            {pkg.type === 'devDependencies' ? 'dev' : 'prod'}
                          </span>
                        </td>
                        <td style={tdStyle}>
                          {pkg.isMajor ? (
                            <span style={{
                              fontSize: 11, fontWeight: 600, color: '#dc2626',
                              background: '#fef2f2', padding: '2px 8px', borderRadius: 6,
                            }}>
                              ⚠ Major — manual review
                            </span>
                          ) : (
                            <span style={{
                              fontSize: 11, fontWeight: 600, color: '#16a34a',
                              background: '#f0fdf4', padding: '2px 8px', borderRadius: 6,
                            }}>
                              ✓ Safe to update
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Scan History Tab ───────────────────────────────────────────────── */}
      {activeTab === 'history' && (
        <div>
          {history.length === 0 && (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#9ca3af' }}>
              <Clock size={48} style={{ marginBottom: 12 }} />
              <p>No scan history. Run your first security audit above.</p>
            </div>
          )}
          {history.length > 0 && (
            <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f9fafb' }}>
                    <th style={thStyle}>Date</th>
                    <th style={thStyle}>Type</th>
                    <th style={thStyle}>Vulnerabilities</th>
                    <th style={thStyle}>Outdated</th>
                    <th style={thStyle}>Triggered By</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((rec, idx) => (
                    <tr key={idx} style={{ borderTop: '1px solid #f3f4f6' }}>
                      <td style={tdStyle}>{new Date(rec.scanned_at).toLocaleString()}</td>
                      <td style={tdStyle}>
                        <span style={{
                          fontSize: 11, padding: '2px 8px', borderRadius: 6,
                          background: '#eff6ff', color: '#1e40af', fontWeight: 500,
                        }}>
                          {rec.scan_type?.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td style={tdStyle}>
                        {rec.total_vulnerabilities > 0 ? (
                          <span style={{ color: '#dc2626', fontWeight: 600 }}>
                            {rec.total_vulnerabilities} ({rec.critical_count}C/{rec.high_count}H/{rec.moderate_count}M/{rec.low_count}L)
                          </span>
                        ) : (
                          <span style={{ color: '#16a34a' }}>0</span>
                        )}
                      </td>
                      <td style={tdStyle}>{rec.outdated_count || '—'}</td>
                      <td style={tdStyle}>
                        <span style={{ fontSize: 12, color: '#6b7280' }}>{rec.triggered_by}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Spinner animation ──────────────────────────────────────────────── */}
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .spin { animation: spin 1s linear infinite; }
      `}</style>
    </div>
  );
}

// ── Shared styles ────────────────────────────────────────────────────────────
function btnStyle(color) {
  return {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '8px 16px', borderRadius: 8, border: 'none',
    background: color, color: '#fff', fontSize: 13, fontWeight: 600,
    cursor: 'pointer', transition: 'opacity 0.15s',
  };
}

const thStyle = {
  textAlign: 'left', padding: '10px 14px', fontSize: 12, fontWeight: 600,
  color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em',
};

const tdStyle = { padding: '10px 14px' };
